import { Plus, Trash2 } from "lucide-react";
import type { Clause } from "../../../_services/RulesService";
import ClauseForm, { defaultClauseForType } from "./ClauseForm";

interface Props {
  clause: Clause;
  onChange: (next: Clause) => void;
  /** Optional: shown when this is the root node — prevents removing the only clause. */
  isRoot?: boolean;
  /** Callback to remove this clause from its parent — undefined on root. */
  onRemove?: () => void;
}

/**
 * Recursive predicate editor. Renders AND/OR/NOT composites as nested
 * cards with "Add clause" / "Wrap in NOT" / "Convert to AND/OR" actions,
 * and leaf clauses via ClauseForm.
 */
const PredicateBuilder: React.FC<Props> = ({ clause, onChange, isRoot, onRemove }) => {
  if ("op" in clause) {
    if (clause.op === "and" || clause.op === "or") {
      const op = clause.op;
      const flip = op === "and" ? "or" : "and";
      return (
        <div className={`bm-pred-composite is-${op}`}>
          <div className="bm-pred-head">
            <span className={`bm-pred-op-pill is-${op}`}>{op.toUpperCase()}</span>
            <div className="bm-pred-head-actions">
              <button
                type="button"
                className="bm-pred-iconbtn"
                title={`Convert to ${flip.toUpperCase()}`}
                onClick={() => onChange({ ...clause, op: flip } as Clause)}
              >
                ⇄ {flip.toUpperCase()}
              </button>
              <button
                type="button"
                className="bm-pred-iconbtn"
                title="Wrap in NOT"
                onClick={() => onChange({ op: "not", clause } as Clause)}
              >
                NOT
              </button>
              {!isRoot && onRemove && (
                <button type="button" className="bm-pred-iconbtn danger" title="Remove" onClick={onRemove}>
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          </div>
          <div className="bm-pred-children">
            {clause.clauses.map((child, idx) => (
              <PredicateBuilder
                key={idx}
                clause={child}
                onChange={(next) => {
                  const arr = clause.clauses.slice();
                  arr[idx] = next;
                  onChange({ ...clause, clauses: arr });
                }}
                onRemove={
                  clause.clauses.length > 1
                    ? () => {
                        const arr = clause.clauses.slice();
                        arr.splice(idx, 1);
                        onChange({ ...clause, clauses: arr });
                      }
                    : undefined
                }
              />
            ))}
          </div>
          <button
            type="button"
            className="bm-pred-add-clause"
            onClick={() =>
              onChange({
                ...clause,
                clauses: [...clause.clauses, defaultClauseForType("scope_completed")],
              })
            }
          >
            <Plus size={12} /> Add clause
          </button>
        </div>
      );
    }
    if (clause.op === "not") {
      return (
        <div className="bm-pred-composite is-not">
          <div className="bm-pred-head">
            <span className="bm-pred-op-pill is-not">NOT</span>
            <div className="bm-pred-head-actions">
              <button
                type="button"
                className="bm-pred-iconbtn"
                title="Remove NOT wrapper"
                onClick={() => onChange(clause.clause)}
              >
                Unwrap
              </button>
              {!isRoot && onRemove && (
                <button type="button" className="bm-pred-iconbtn danger" title="Remove" onClick={onRemove}>
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          </div>
          <div className="bm-pred-children">
            <PredicateBuilder
              clause={clause.clause}
              onChange={(next) => onChange({ op: "not", clause: next } as Clause)}
            />
          </div>
        </div>
      );
    }
  }

  // Leaf clause
  return (
    <div className="bm-pred-leaf">
      <div className="bm-pred-leaf-head">
        <div className="bm-pred-leaf-actions">
          <button
            type="button"
            className="bm-pred-iconbtn"
            title="Wrap in AND group"
            onClick={() => onChange({ op: "and", clauses: [clause, defaultClauseForType("scope_completed")] } as Clause)}
          >
            Group AND
          </button>
          <button
            type="button"
            className="bm-pred-iconbtn"
            title="Wrap in OR group"
            onClick={() => onChange({ op: "or", clauses: [clause, defaultClauseForType("scope_completed")] } as Clause)}
          >
            Group OR
          </button>
          <button
            type="button"
            className="bm-pred-iconbtn"
            title="Wrap in NOT"
            onClick={() => onChange({ op: "not", clause } as Clause)}
          >
            NOT
          </button>
          {!isRoot && onRemove && (
            <button type="button" className="bm-pred-iconbtn danger" title="Remove" onClick={onRemove}>
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>
      <ClauseForm clause={clause} onChange={onChange} />
    </div>
  );
};

export default PredicateBuilder;
